// The `form.draft` card (SCRUM-278): an editable, prefilled form the clinic
// reads, corrects and approves. Nothing is written until "Aprovar".
//
// Contract: services/copilot/contracts/cards/schema.json, kind `form.draft`.
// The approval leaves as the draft's own `data` with the edited keys replaced
// (docs/architecture/specs/copilot-form-draft-card.md §3), so this view emits
// values keyed by field key and never composes a payload of its own. Keys in
// `server_owned` have no field and are never rendered.
//
// A refused write (`TOOL_VALIDATION_ERROR`, spec §3.1) arrives as `failure`:
// the card reopens in edit mode with one message per field, and "Aprovar"
// stays blocked until the clinic changes the fields the runtime named.

import { useMemo, useState } from "react";
import { AlertTriangle, Check, FileText, Pencil, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
  CardActionHandler,
  CardApprovalFailure,
  CardViewState,
  FormDraftCardEnvelope,
} from "@/components/generative-ui/envelope/types";
import { cn } from "@/lib/utils";

import { FormFieldRow } from "./FormField";
import { displayValue, payloadValue } from "./masks";
import { fieldErrors } from "./validate";

const DOCUMENT_EYEBROW = "Extraído do documento — confira";

const STATUS_LABEL: Record<Exclude<CardViewState, "pending">, string> = {
  approved: "Aprovado",
  rejected: "Cancelado",
  failed: "Não concluído",
};

const STATUS_TONE: Record<Exclude<CardViewState, "pending">, string> = {
  approved: "border-app-success/40 text-app-success",
  rejected: "border-app-border text-app-fg-muted",
  failed: "border-app-danger/40 text-app-danger",
};

function initialValues(card: FormDraftCardEnvelope): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of card.fields) {
    values[field.key] = displayValue(field.type, field.value);
  }
  return values;
}

export function FormDraftCardView({
  card,
  onAction,
  state = "pending",
  failure,
  disabled = false,
  approvedLabel = "Aprovado",
}: {
  card: FormDraftCardEnvelope;
  onAction?: CardActionHandler;
  state?: CardViewState;
  failure?: CardApprovalFailure;
  disabled?: boolean;
  approvedLabel?: string;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(card));
  const [editing, setEditing] = useState(false);
  // The messages the runtime sent back, minus the ones the clinic already
  // edited away. State, not derived: a fix has to free "Aprovar" before the
  // next round trip, which is the whole point of the failed state.
  const [serverErrors, setServerErrors] = useState<Record<string, readonly string[]>>({});
  const [appliedFailure, setAppliedFailure] = useState<string | null>(null);

  // A new refusal reopens the form with its messages. Adjusting state during
  // the render is React's own answer to "a prop changed"; an effect would
  // paint the stale card first.
  const failureKey = failure ? JSON.stringify(failure) : null;
  if (failureKey !== appliedFailure) {
    setAppliedFailure(failureKey);
    setServerErrors(failure?.fields ?? {});
    if (failure) setEditing(true);
  }

  const visibleFields = useMemo(
    () => card.fields.filter((field) => !card.server_owned.includes(field.key)),
    [card.fields, card.server_owned],
  );
  const localErrors = useMemo(() => fieldErrors(visibleFields, values), [visibleFields, values]);
  const fieldKeys = useMemo(
    () => new Set(visibleFields.map((field) => field.key)),
    [visibleFields],
  );

  const errors: Record<string, string> = { ...localErrors };
  const unknownKeyWarnings: string[] = [];
  let blockedByServer = false;
  for (const [key, messages] of Object.entries(serverErrors)) {
    if (!fieldKeys.has(key)) {
      // A key no field matches: rendering it under the API name beats losing
      // what the API said (spec §3.1).
      unknownKeyWarnings.push(`${key}: ${messages.join(" ")}`);
      continue;
    }
    blockedByServer = true;
    errors[key] = errors[key] ?? messages.join(" ");
  }

  const hasErrors = Object.keys(localErrors).length > 0 || blockedByServer;
  const fromDocument = visibleFields.some((field) => field.source === "document");
  const warnings = [...card.warnings, ...unknownKeyWarnings];
  // A refusal does not close the card: it reopens it carrying the messages.
  const reopened = state === "failed" && failure !== undefined;

  if (state !== "pending" && !reopened) {
    return (
      <div
        data-testid="form-draft-card"
        className="rounded-xl border border-app-border bg-app-surface p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium text-app-fg-strong">{card.title}</p>
          <span
            className={cn(
              "shrink-0 rounded-full border px-2 py-0.5 text-[11px]",
              STATUS_TONE[state],
            )}
          >
            {state === "approved" ? approvedLabel : STATUS_LABEL[state]}
          </span>
        </div>
        <dl className="mt-2 space-y-1">
          {visibleFields
            .filter((field) => (values[field.key] ?? "").length > 0)
            .map((field) => (
              <div key={field.key} className="flex gap-2 text-xs">
                <dt className="text-app-fg-muted">{field.label}:</dt>
                <dd className="min-w-0 truncate text-app-fg-strong">{values[field.key]}</dd>
              </div>
            ))}
        </dl>
      </div>
    );
  }

  const changeField = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
    setServerErrors((current) => {
      if (!(key in current)) return current;
      const rest = { ...current };
      delete rest[key];
      return rest;
    });
  };

  const approve = () => {
    if (hasErrors || disabled) return;
    const edited: Record<string, string | null> = {};
    for (const field of visibleFields) {
      edited[field.key] = payloadValue(field.type, values[field.key] ?? "");
    }
    onAction?.({
      intent: "approve_form_draft",
      payload: { card_id: card.card_id, action: card.action, values: edited },
    });
  };

  return (
    <div
      data-testid="form-draft-card"
      className="rounded-xl border border-app-border bg-app-surface p-4"
    >
      {fromDocument && (
        <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-app-fg-muted">
          <FileText className="h-3 w-3" aria-hidden="true" />
          {DOCUMENT_EYEBROW}
        </p>
      )}

      <p className="text-sm font-medium text-app-fg-strong">{card.title}</p>
      <p className="mt-0.5 text-xs text-app-fg-muted">{card.summary}</p>

      {failure && (
        <p
          role="alert"
          data-testid="form-draft-failure"
          className="mt-3 rounded-lg border border-app-danger/40 bg-app-danger/10 px-3 py-2 text-xs text-app-fg-strong"
        >
          {failure.userMessage}
        </p>
      )}

      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1">
          {warnings.map((warning) => (
            <li key={warning} className="flex items-start gap-1.5 text-xs text-app-warning">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      )}

      <fieldset disabled={disabled} className="mt-3 space-y-3">
        {visibleFields.map((field) => (
          <FormFieldRow
            key={field.key}
            field={field}
            value={values[field.key] ?? ""}
            error={errors[field.key]}
            editing={editing}
            onChange={(next) => changeField(field.key, next)}
          />
        ))}
      </fieldset>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="petrol" size="sm" disabled={hasErrors || disabled || !onAction} onClick={approve}>
          <Check aria-hidden="true" />
          {card.confirm_label}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing((current) => !current)}
          aria-pressed={editing}
          disabled={disabled}
        >
          <Pencil aria-hidden="true" />
          {editing ? "Concluir edição" : "Editar"}
        </Button>
        <Button
          variant="link"
          size="sm"
          className="ml-auto text-app-fg-muted"
          disabled={disabled}
          onClick={() =>
            onAction?.({ intent: "cancel_form_draft", payload: { card_id: card.card_id } })
          }
        >
          <X aria-hidden="true" />
          Cancelar
        </Button>
      </div>
    </div>
  );
}
